import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CommentableType } from '@spraypaint/Comment';
import { CommentsModal } from '../CommentsModal';
import { useAddComment } from './useAddComment';
import { useGetComments } from './useGetComments';
import { CommentFormValue } from '../CommentList';

type Params = {
  commentableId?: string;
  commentableType: CommentableType;
  title?: string;
};

export const useCommentModal = ({
  commentableId,
  commentableType,
  title,
}: Params) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const invalidateComments = useCallback(
    () =>
      queryClient.invalidateQueries([
        'get-comments',
        commentableId,
        commentableType,
      ]),
    [queryClient],
  );

  const { comments } = useGetComments({
    commentableType,
    commentableId,
  });

  const {
    mutate: addComment,
    isLoading: isSubmitting,
    isSuccess,
  } = useAddComment(invalidateComments);

  const handleSubmit = useCallback(
    (commentFormData: CommentFormValue) => {
      addComment({
        commentableId,
        content: commentFormData.content,
        commentableType,
      });
    },
    [addComment, commentableId, commentableType],
  );

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  const modalProps = useMemo(
    () => ({
      isOpen,
      onRequestClose: handleClose,
    }),
    [handleClose, isOpen],
  );

  const ModalComponent = () => (
    <CommentsModal
      modalProps={modalProps}
      comments={comments}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isSuccess={isSuccess}
      title={title}
    />
  );

  return {
    onClick: handleOpen,
    commentsCount: comments?.length ?? 0,
    CommentsModal: ModalComponent,
  };
};
